import { create } from 'zustand';
import { format } from 'date-fns';
import type {
  Habit,
  HabitCreate,
  HabitUpdate,
  HabitCompletion,
  HabitStats,
} from '@/types/habit';
import * as firestoreHabits from '@/api/firestore/habits';
import { calculateStreaks, type StreakData } from '@/utils/streaks';
import { isMetaBuiltInHabit } from '@/utils/metaBuiltInHabit';
import { logicalToday } from '@/utils/dayBoundary';
import { getPeriodBounds } from '@/utils/habitPeriod';
import { useSettingsStore } from '@/stores/settingsStore';
import {
  selectForgivenessConfig,
  useAdvancedTuningStore,
} from '@/stores/advancedTuningStore';
import { useRestDayStore } from '@/stores/restDayStore';
import { useHabitLogStore } from '@/stores/habitLogStore';
import type { Unsubscribe } from 'firebase/firestore';

interface HabitState {
  habits: Habit[];
  /** habitId → completions list */
  completions: Record<string, HabitCompletion[]>;
  /** habitId → stats from backend */
  stats: Record<string, HabitStats>;
  selectedHabit: Habit | null;
  isLoading: boolean;
  error: string | null;

  fetchHabits: () => Promise<void>;
  fetchCompletionsForHabit: (habitId: string) => Promise<void>;
  fetchAllCompletions: () => Promise<void>;
  fetchHabitStats: (habitId: string) => Promise<HabitStats>;
  createHabit: (data: HabitCreate) => Promise<Habit>;
  updateHabit: (habitId: string, data: HabitUpdate) => Promise<Habit>;
  deleteHabit: (habitId: string) => Promise<void>;
  toggleCompletion: (habitId: string, date: string) => Promise<void>;
  setSelectedHabit: (habit: Habit | null) => void;
  clearError: () => void;

  // Real-time
  subscribeToHabits: (uid: string) => Unsubscribe;
  subscribeToCompletions: (uid: string) => Unsubscribe;

  // Computed helpers
  getStreakData: (habitId: string) => StreakData | null;
  isTodayCompleted: (habitId: string) => boolean;
  getTodayCount: (habitId: string) => number;
  /**
   * Completion count inside the habit's current period. For `daily`
   * habits this is today's count (period = day); for non-daily habits
   * it is the sum of completions inside the matching weekly /
   * fortnightly / monthly / bimonthly / quarterly window — the same
   * `periodCompletions` Android surfaces on the habit card.
   */
  getPeriodCompletions: (habitId: string) => number;
  /**
   * Count of strictly *future* bookings (logs) inside the habit's current
   * period — sessions the user has scheduled via `HabitBookingDialog` but
   * has not yet completed. Always `0` for non-bookable habits, and `0`
   * for daily habits (their "period" is a single day, so no future slots
   * exist inside it).
   */
  getPeriodBookings: (habitId: string) => number;
  getTodayProgress: () => { completed: number; total: number };
  getWeekCompletions: (habitId: string) => boolean[];
  /**
   * Per-weekday booleans (Mon..Sun) marking days with a future booking
   * but no completion. Lets the week-dot UI render booked-but-pending
   * days distinctly from past completions. Always all-false for
   * non-bookable habits.
   */
  getWeekBookings: (habitId: string) => boolean[];
}

import { getFirebaseUid } from '@/stores/firebaseUid';

function getUid(): string {
  return getFirebaseUid();
}

function parseActiveDays(json: string | null): number[] | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Sum a non-daily habit's completion counts inside the period containing
 * `todayIso`. Caller is responsible for branching on `habit.frequency` —
 * passing a daily habit throws (see `getPeriodBounds`).
 */
function countPeriodCompletions(
  habit: Habit,
  completions: HabitCompletion[],
  todayIso: string,
): number {
  const { startIso, endIso } = getPeriodBounds(habit.frequency, todayIso);
  let total = 0;
  for (const c of completions) {
    if (c.date >= startIso && c.date <= endIso) total += c.count;
  }
  return total;
}

/**
 * Count bookable-habit logs (from `habitLogStore`) whose logical day falls
 * in `[startIso, endIso]` AND is on or before `todayIso`. Bookings made via
 * `HabitBookingDialog` write to `habit_logs` only, never to
 * `habit_completions`; without folding past/today logs in here the "have I
 * done this within the period" check would answer No for any bookable
 * habit the user has only booked. Mirrors the `lastLogDate` recency signal
 * Android uses in `HabitRepository.kt:491` + `TodayViewModel.kt:853-872`.
 *
 * Future-dated logs (e.g. a therapy session booked for next Friday) are
 * intentionally excluded — a booking that hasn't happened yet is not a
 * completion. They're surfaced separately via `countFutureLogsInRange`
 * so the UI can render them with a distinct affordance instead of
 * conflating them with completed sessions.
 *
 * Non-bookable habits short-circuit to `0` so the legacy completion-only
 * counts stay exact for them.
 */
function countLogsInRange(
  habit: Habit,
  startIso: string,
  endIso: string,
  todayIso: string,
  sodHour: number,
): number {
  if (!habit.is_bookable) return 0;
  const logs = useHabitLogStore.getState().getLogsFor(habit.id);
  if (logs.length === 0) return 0;
  let count = 0;
  for (const log of logs) {
    const iso = logicalToday(log.date, sodHour);
    if (iso >= startIso && iso <= endIso && iso <= todayIso) count++;
  }
  return count;
}

/**
 * Companion to {@link countLogsInRange} that counts strictly *future*
 * bookings in the period — logs whose logical day is after `todayIso` but
 * still within `[startIso, endIso]`. Powers the "X booked" affordance on
 * recurring habit cards so users can tell at a glance how many sessions
 * they've actually completed vs. how many are scheduled but pending.
 */
function countFutureLogsInRange(
  habit: Habit,
  startIso: string,
  endIso: string,
  todayIso: string,
  sodHour: number,
): number {
  if (!habit.is_bookable) return 0;
  const logs = useHabitLogStore.getState().getLogsFor(habit.id);
  if (logs.length === 0) return 0;
  let count = 0;
  for (const log of logs) {
    const iso = logicalToday(log.date, sodHour);
    if (iso > todayIso && iso >= startIso && iso <= endIso) count++;
  }
  return count;
}

/**
 * Logical "today" ISO (`YYYY-MM-DD`) honouring the user's Start-of-Day
 * hour. Reads `startOfDayHour` from the settings store at call time so
 * the value stays in sync with cross-device updates.
 *
 * The bug this fixes: previously this returned the calendar-midnight
 * date via `format(new Date(), 'yyyy-MM-dd')`. Android writes
 * `completedDateLocal` via `DayBoundary.normalizeToDayStart` (SoD-aware,
 * see `HabitRepository.completeHabit` → `epochToLocalDateString`). For a
 * user with SoD = 4 completing a habit at 02:00 local, Android stamps
 * yesterday's date (logical day started at 04:00 yesterday) but the web
 * `isTodayCompleted` / `getTodayCount` getters were looking up *today's*
 * calendar date, so the completion appeared to vanish on web.
 */
function todayStr(): string {
  const hour = useSettingsStore.getState().startOfDayHour;
  return logicalToday(Date.now(), hour);
}

export const useHabitStore = create<HabitState>((set, get) => ({
  habits: [],
  completions: {},
  stats: {},
  selectedHabit: null,
  isLoading: false,
  error: null,

  fetchHabits: async () => {
    set({ isLoading: true, error: null });
    try {
      const uid = getUid();
      const habits = await firestoreHabits.getHabits(uid);
      set({ habits, isLoading: false });

      // Fetch all completions
      await get().fetchAllCompletions();
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  fetchCompletionsForHabit: async (habitId) => {
    try {
      const uid = getUid();
      const completionsList = await firestoreHabits.getCompletions(uid, habitId);
      set((state) => ({
        completions: {
          ...state.completions,
          [habitId]: completionsList,
        },
      }));
    } catch {
      // Silently fail for individual habit fetches
    }
  },

  fetchAllCompletions: async () => {
    try {
      const uid = getUid();
      const allCompletions = await firestoreHabits.getAllCompletions(uid);
      // Group completions by habit_id
      const grouped: Record<string, HabitCompletion[]> = {};
      for (const c of allCompletions) {
        if (!grouped[c.habit_id]) grouped[c.habit_id] = [];
        grouped[c.habit_id].push(c);
      }
      set({ completions: grouped });
    } catch {
      // Silently fail
    }
  },

  fetchHabitStats: async (habitId) => {
    // Compute stats client-side from completions
    const completions = get().completions[habitId] || [];
    const streakData = get().getStreakData(habitId);
    const stats: HabitStats = {
      habit_id: habitId,
      current_streak: streakData?.currentStreak ?? 0,
      longest_streak: streakData?.longestStreak ?? 0,
      total_completions: completions.reduce((sum, c) => sum + c.count, 0),
      completion_rate: 0,
      completions_this_week: 0,
    };
    set((state) => ({
      stats: { ...state.stats, [habitId]: stats },
    }));
    return stats;
  },

  createHabit: async (data) => {
    const uid = getUid();
    const habit = await firestoreHabits.createHabit(uid, data);
    set((state) => ({
      habits: [...state.habits, habit],
      completions: { ...state.completions, [habit.id]: [] },
    }));
    return habit;
  },

  updateHabit: async (habitId, data) => {
    const uid = getUid();
    const updated = await firestoreHabits.updateHabit(uid, habitId, data as Record<string, unknown>);
    set((state) => ({
      habits: state.habits.map((h) => (h.id === habitId ? updated : h)),
    }));
    return updated;
  },

  deleteHabit: async (habitId) => {
    const uid = getUid();
    await firestoreHabits.deleteHabit(uid, habitId);
    set((state) => {
      const newCompletions = { ...state.completions };
      delete newCompletions[habitId];
      const newStats = { ...state.stats };
      delete newStats[habitId];
      return {
        habits: state.habits.filter((h) => h.id !== habitId),
        completions: newCompletions,
        stats: newStats,
      };
    });
  },

  toggleCompletion: async (habitId, date) => {
    const state = get();
    const existing = (state.completions[habitId] || []).find(
      (c) => c.date === date,
    );

    // Optimistic update
    if (existing && existing.count > 0) {
      set((s) => ({
        completions: {
          ...s.completions,
          [habitId]: (s.completions[habitId] || []).filter(
            (c) => c.date !== date,
          ),
        },
      }));
    } else {
      const optimistic: HabitCompletion = {
        id: `temp_${Date.now()}`,
        habit_id: habitId,
        date,
        count: 1,
        created_at: new Date().toISOString(),
      };
      set((s) => ({
        completions: {
          ...s.completions,
          [habitId]: [...(s.completions[habitId] || []), optimistic],
        },
      }));
    }

    try {
      const uid = getUid();
      const result = await firestoreHabits.toggleCompletion(uid, habitId, date);

      if (result.action === 'removed') {
        set((s) => ({
          completions: {
            ...s.completions,
            [habitId]: (s.completions[habitId] || []).filter(
              (c) => c.date !== date,
            ),
          },
        }));
      } else if (result.completion) {
        set((s) => ({
          completions: {
            ...s.completions,
            [habitId]: [
              ...(s.completions[habitId] || []).filter(
                (c) => c.date !== date,
              ),
              result.completion!,
            ],
          },
        }));
      }
    } catch {
      // Revert on error by re-fetching
      await get().fetchCompletionsForHabit(habitId);
    }
  },

  setSelectedHabit: (habit) => set({ selectedHabit: habit }),
  clearError: () => set({ error: null }),

  subscribeToHabits: (uid: string) => {
    return firestoreHabits.subscribeToHabits(uid, (habits) => {
      set({ habits });
    });
  },

  subscribeToCompletions: (uid: string) => {
    return firestoreHabits.subscribeToCompletions(uid, (allCompletions) => {
      const grouped: Record<string, HabitCompletion[]> = {};
      for (const c of allCompletions) {
        if (!grouped[c.habit_id]) grouped[c.habit_id] = [];
        grouped[c.habit_id].push(c);
      }
      set({ completions: grouped });
    });
  },

  getStreakData: (habitId) => {
    const state = get();
    const habit = state.habits.find((h) => h.id === habitId);
    const completions = state.completions[habitId];
    if (!habit || !completions) return null;

    // Per-user forgiveness knobs from Settings → Advanced Tuning. Reads
    // the store imperatively so the streak picks up cross-device
    // tweaks immediately without re-subscribing this component graph.
    // When Advanced Tuning hasn't loaded yet, the store's default state
    // already mirrors `DEFAULT_FORGIVENESS`, so the streak math degrades
    // gracefully to the same numbers callers used to see pre-PR.
    const forgiveness = selectForgivenessConfig(
      useAdvancedTuningStore.getState().prefs,
    );

    // Rest days fold into the daily forgiveness walk as
    // kept-by-definition (`docs/REST_DAY.md` § *The core rule*). Pulled
    // at read time from the restDay store so a fresh rest-day mark
    // surfaces in the streak immediately — the snapshot listener wired
    // in `useFirestoreSync` keeps the underlying set in sync.
    const restDays = useRestDayStore.getState().restDates;

    // Per-habit streak-forgiveness overrides (parity audit § B.6).
    // Mirrors Android's `HabitForgivenessResolver`: any of the four
    // fields set on the habit takes precedence over the global config
    // for this habit only. `undefined` fields inherit. The resolver
    // inside `calculateStreaks` does the field-by-field substitution.
    const habitOverrides = {
      streakMaxMissedDays: habit.streak_max_missed_days,
      forgivenessEnabled: habit.forgiveness_enabled,
      forgivenessAllowedMisses: habit.forgiveness_allowed_misses,
      forgivenessGracePeriodDays: habit.forgiveness_grace_period_days,
    };

    return calculateStreaks(
      completions.map((c) => ({ date: c.date, count: c.count })),
      habit.frequency,
      parseActiveDays(habit.active_days_json),
      habit.target_count,
      forgiveness,
      restDays,
      undefined, // taskMode — habits don't carry one
      habitOverrides,
    );
  },

  isTodayCompleted: (habitId) => {
    const state = get();
    const habit = state.habits.find((h) => h.id === habitId);
    if (!habit) return false;
    const completions = state.completions[habitId] || [];
    const today = todayStr();
    const sodHour = useSettingsStore.getState().startOfDayHour;
    const target = habit.target_count || 1;
    if (habit.frequency === 'daily') {
      const todayCompletion = completions.find((c) => c.date === today);
      const count =
        (todayCompletion?.count || 0) +
        countLogsInRange(habit, today, today, today, sodHour);
      return count >= target;
    }
    // Non-daily: "completed today" means the period's target is met —
    // mirrors Android's `HabitRepository.getHabitsWithFullStatus` branch
    // (`periodCompletions >= habit.targetFrequency`). target_count on a
    // non-daily habit is the per-period total, not a per-day target.
    // Future bookings inside the period intentionally don't count — a
    // session you've scheduled for next Friday isn't a session you've
    // already done. See `countLogsInRange` for the rationale.
    const { startIso, endIso } = getPeriodBounds(habit.frequency, today);
    const periodCount =
      countPeriodCompletions(habit, completions, today) +
      countLogsInRange(habit, startIso, endIso, today, sodHour);
    return periodCount >= target;
  },

  getTodayCount: (habitId) => {
    const state = get();
    const habit = state.habits.find((h) => h.id === habitId);
    if (!habit) return 0;
    const completions = state.completions[habitId] || [];
    const today = todayStr();
    const sodHour = useSettingsStore.getState().startOfDayHour;
    const todayCompletion = completions.find((c) => c.date === today);
    return (
      (todayCompletion?.count || 0) +
      countLogsInRange(habit, today, today, today, sodHour)
    );
  },

  getPeriodCompletions: (habitId) => {
    const state = get();
    const habit = state.habits.find((h) => h.id === habitId);
    if (!habit) return 0;
    const completions = state.completions[habitId] || [];
    const today = todayStr();
    const sodHour = useSettingsStore.getState().startOfDayHour;
    if (habit.frequency === 'daily') {
      const todayCompletion = completions.find((c) => c.date === today);
      return (
        (todayCompletion?.count || 0) +
        countLogsInRange(habit, today, today, today, sodHour)
      );
    }
    const { startIso, endIso } = getPeriodBounds(habit.frequency, today);
    return (
      countPeriodCompletions(habit, completions, today) +
      countLogsInRange(habit, startIso, endIso, today, sodHour)
    );
  },

  getPeriodBookings: (habitId) => {
    const state = get();
    const habit = state.habits.find((h) => h.id === habitId);
    if (!habit) return 0;
    // Daily habits have a single-day "period" — there's no future inside
    // it that's also still inside the period. Short-circuit so callers
    // don't have to special-case it.
    if (habit.frequency === 'daily') return 0;
    const today = todayStr();
    const sodHour = useSettingsStore.getState().startOfDayHour;
    const { startIso, endIso } = getPeriodBounds(habit.frequency, today);
    return countFutureLogsInRange(habit, startIso, endIso, today, sodHour);
  },

  getTodayProgress: () => {
    const state = get();
    // Exclude the six built-in meta-habits (Morning/Bedtime Self-Care,
    // Medication, Housework, School, Leisure). Each surfaces elsewhere
    // (dedicated Today cards or top-level Medication destination) — see
    // Android's `HabitListViewModel.kt:213-221`. Keeps the Today progress
    // count consistent with the Today habit section, done-counter sheet,
    // and Habits screen.
    const activeHabits = state.habits.filter(
      (h) => h.is_active && !isMetaBuiltInHabit(h),
    );
    const today = todayStr();
    // Use the logical-day Date (parsed from `today`) so the active-day
    // weekday filter aligns with Android's `DayBoundary`-driven
    // semantics. A user with SoD = 4 doing their habit at 02:00 should
    // still see Monday-only habits as "today" if the logical day is
    // Monday.
    const todayDate = new Date(today + 'T12:00:00');
    const sodHour = useSettingsStore.getState().startOfDayHour;
    let total = 0;
    let completed = 0;

    for (const habit of activeHabits) {
      const activeDays = parseActiveDays(habit.active_days_json);
      if (activeDays && activeDays.length > 0) {
        const jsDay = todayDate.getDay();
        const isoDay = jsDay === 0 ? 7 : jsDay;
        if (!activeDays.includes(isoDay)) continue;
      }
      total++;
      if (habit.frequency !== 'daily') {
        // Period-aware (week / fortnight / month / 2 months / quarter)
        // — mirrors Android `HabitRepository.getHabitsWithFullStatus`.
        // Bookable habits fold past/today `habit_logs` into the period
        // count so bookings made via `HabitBookingDialog` register as
        // "done" alongside checkbox-driven completions. Future bookings
        // are excluded — a session scheduled for next Friday isn't a
        // session you've already completed today.
        const { startIso, endIso } = getPeriodBounds(habit.frequency, today);
        const periodCount =
          countPeriodCompletions(
            habit,
            state.completions[habit.id] || [],
            today,
          ) + countLogsInRange(habit, startIso, endIso, today, sodHour);
        if (periodCount >= habit.target_count) completed++;
      } else {
        const todayCompletion = (state.completions[habit.id] || []).find(
          (c) => c.date === today,
        );
        const dailyCount =
          (todayCompletion?.count || 0) +
          countLogsInRange(habit, today, today, today, sodHour);
        if (dailyCount >= habit.target_count) completed++;
      }
    }

    return { completed, total };
  },

  getWeekCompletions: (habitId) => {
    const state = get();
    const habit = state.habits.find((h) => h.id === habitId);
    const completions = state.completions[habitId] || [];
    const sodHour = useSettingsStore.getState().startOfDayHour;
    const todayIso = todayStr();
    // Bookable habits fold past/today `habit_logs` into the per-day
    // dots so a booked day lights up even with no checkbox completion.
    // Future-dated logs are intentionally excluded — they're surfaced
    // separately by `getWeekBookings` so the UI can render them with a
    // distinct affordance. Logs carry wall-clock epoch ms — re-key to a
    // logical-day ISO so the SoD-aware semantics match
    // `getTodayCount` / `isTodayCompleted`.
    const logIsoSet = new Set<string>();
    if (habit?.is_bookable) {
      const logs = useHabitLogStore.getState().getLogsFor(habitId);
      for (const log of logs) {
        const iso = logicalToday(log.date, sodHour);
        if (iso <= todayIso) logIsoSet.add(iso);
      }
    }
    const today = new Date();
    const dayOffset = (today.getDay() + 6) % 7;
    const result: boolean[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - dayOffset + i);
      const dateStr = format(d, 'yyyy-MM-dd');
      const fromCompletions = completions.some(
        (c) => c.date === dateStr && c.count > 0,
      );
      result.push(fromCompletions || logIsoSet.has(dateStr));
    }
    return result;
  },

  getWeekBookings: (habitId) => {
    const state = get();
    const habit = state.habits.find((h) => h.id === habitId);
    const sodHour = useSettingsStore.getState().startOfDayHour;
    const todayIso = todayStr();
    const result: boolean[] = Array(7).fill(false);
    if (!habit?.is_bookable) return result;
    const completions = state.completions[habitId] || [];
    const futureBookingDays = new Set<string>();
    const logs = useHabitLogStore.getState().getLogsFor(habitId);
    for (const log of logs) {
      const iso = logicalToday(log.date, sodHour);
      if (iso > todayIso) futureBookingDays.add(iso);
    }
    const today = new Date();
    const dayOffset = (today.getDay() + 6) % 7;
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - dayOffset + i);
      const dateStr = format(d, 'yyyy-MM-dd');
      // A day with a completion outranks a same-day booking — if you
      // somehow have both, render it as completed not booked.
      const hasCompletion = completions.some(
        (c) => c.date === dateStr && c.count > 0,
      );
      result[i] = !hasCompletion && futureBookingDays.has(dateStr);
    }
    return result;
  },
}));
